import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { DesktopLayout, StandingsSidebar, LiveAnswer as SharedLiveAnswer, TimerSection } from '../shared';
import { WouldYouRatherResultScreen } from './result-screens/WouldYouRatherResultScreen';
import { GameEndScreen } from './GameEndScreen';
import type { GameStatistics } from '../../types/game';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface WouldYouRatherQuestion {
  questionId?: string;
  id?: string;
  question: string;
  optionA: string;
  optionB: string;
  options: string[]; // [optionA, optionB]
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  isWouldYouRather?: boolean;
}

interface DrinkerEntry {
  playerId: string;
  playerName: string;
  avatar?: string;
  vote: 'AGREE' | 'DISAGREE';
}

interface QuestionResult {
  questionId: string;
  optionA?: string;
  optionB?: string;
  optionACount?: number;
  optionBCount?: number;
  minorityChoice?: string | 'tie';
  debatePrompt?: string;
  drinkers?: DrinkerEntry[];
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  isWouldYouRather?: boolean;
}

interface LiveAnswer {
  playerId: string;
  playerName: string;
  avatar?: string;
  timestamp: number;
  answer?: string;
}

interface WouldYouRatherHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: WouldYouRatherQuestion;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
}

// Theme colors for Would You Rather (Party theme - purple)
const THEME = {
  primary: '#A855F7',
  primaryLight: 'rgba(168, 85, 247, 0.15)',
  primaryBorder: 'rgba(168, 85, 247, 0.3)',
  accent: '#EC4899',
  accentLight: 'rgba(236, 72, 153, 0.15)',
  accentBorder: 'rgba(236, 72, 153, 0.3)',
  optionA: '#22C55E',
  optionALight: 'rgba(34, 197, 94, 0.15)',
  optionABorder: 'rgba(34, 197, 94, 0.3)',
  optionB: '#EF4444',
  optionBLight: 'rgba(239, 68, 68, 0.15)',
  optionBBorder: 'rgba(239, 68, 68, 0.3)',
};

const WouldYouRatherHostDisplay: React.FC<WouldYouRatherHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  myPlayerId,
  hostIsPlaying = false,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<WouldYouRatherQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [waitingForNewGame, setWaitingForNewGame] = useState(false);
  const [liveAnswers, setLiveAnswers] = useState<LiveAnswer[]>([]);

  // Host playing mode state
  const [selectedVote, setSelectedVote] = useState<string | null>(null);
  const [voteSubmitted, setVoteSubmitted] = useState(false);
  const isSubmittingRef = useRef<boolean>(false);

  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const roomNameRef = useRef<string>(roomState.roomName);
  const playersRef = useRef(roomState.players);

  const resetHostPlayingState = () => {
    setSelectedVote(null);
    setVoteSubmitted(false);
    setLiveAnswers([]);
    isSubmittingRef.current = false;
  };

  useEffect(() => {
    roomNameRef.current = roomState.roomName;
  }, [roomState.roomName]);

  useEffect(() => {
    playersRef.current = roomState.players;
  }, [roomState.players]);

  useEffect(() => {
    const questionId = roomState.currentQuestion?.questionId || roomState.currentQuestion?.id;
    if (previousQuestionIdRef.current !== questionId && questionId) {
      setCurrentQuestion(roomState.currentQuestion || null);
      setTimeRemaining(roomState.currentQuestion?.timeLimit || 0);
      setQuestionResult(null);
      resetHostPlayingState();
      previousQuestionIdRef.current = questionId;
    }
  }, [roomState.currentQuestion]);

  useEffect(() => {
    const handleQuestion = (data: WouldYouRatherQuestion) => {
      if (data.isWouldYouRather) {
        setCurrentQuestion(data);
        setTimeRemaining(data.timeLimit);
        setQuestionResult(null);
        resetHostPlayingState();
        previousQuestionIdRef.current = data.questionId || data.id;
      }
    };

    const handleQuestionResult = (result: QuestionResult) => {
      if (result.isWouldYouRather) {
        setQuestionResult(result);
        setCurrentQuestion(null);
      }
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: GameStatistics;
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
      setQuestionResult(null);
    };

    const handleReturnToWaiting = () => {
      setWaitingForNewGame(true);
      setGameEnded(false);
      setCurrentQuestion(null);
      setQuestionResult(null);
      setGameStatistics(null);
    };

    const handlePlayerAnswered = (data: LiveAnswer) => {
      setLiveAnswers((prev) => {
        if (prev.some((a) => a.playerId === data.playerId)) return prev;
        return [...prev, data];
      });
    };

    const handleAnswerReceived = (data: {
      questionId: string;
      voteRecorded?: boolean;
      vote?: string;
    }) => {
      if (hostIsPlaying && data.voteRecorded) {
        setVoteSubmitted(true);
      }
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('return-to-waiting', handleReturnToWaiting);
    socket.on('player-answered', handlePlayerAnswered);
    socket.on('answer-received', handleAnswerReceived);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('return-to-waiting', handleReturnToWaiting);
      socket.off('player-answered', handlePlayerAnswered);
      socket.off('answer-received', handleAnswerReceived);
    };
  }, [socket, hostIsPlaying]);

  useEffect(() => {
    if (!currentQuestion || questionResult) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 100) {
          clearInterval(interval);
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentQuestion, questionResult]);

  const handleVoteClick = (vote: string) => {
    if (voteSubmitted || !currentQuestion || isSubmittingRef.current) return;
    
    isSubmittingRef.current = true;
    setSelectedVote(vote);

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      isSubmittingRef.current = false;
      return;
    }

    socket.emit('submit-answer', {
      roomName: roomNameRef.current,
      questionId,
      answer: vote,
      timestamp: Date.now(),
    });
  };

  const handleRestartGame = () => {
    if (socket && roomState) {
      setWaitingForNewGame(true);
      setGameEnded(false);
      setFinalLeaderboard([]);
      setCurrentQuestion(null);
      setQuestionResult(null);
      resetHostPlayingState();
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  // Convert liveAnswers to shared format
  const sharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map(la => ({
    playerId: la.playerId,
    playerName: la.playerName,
    avatar: la.avatar,
    timestamp: la.timestamp,
    answer: la.answer,
  }));

  // Game ended screen
  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        setQuestionResult(null);
        setWaitingForNewGame(true);
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameStatistics}
        onPlaySameGame={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection || (() => {})}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  // Waiting state
  if (waitingForNewGame || (!currentQuestion && !questionResult)) {
    return (
      <div style={{ minHeight: '100vh', width: '100%', background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)', display: 'flex', flexDirection: 'column' }}>
        <Header
          leftButton={onBackToGameSelection ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('gameplay.backToGameSelection') } : undefined}
          rightButton={{ icon: HeaderIcons.refresh, onClick: () => window.location.reload(), tooltip: t('common.tooltips.refreshPage') }}
          roomCode={roomState.roomName}
        />
        <div style={{ flex: 1, padding: '2.5rem 2rem', paddingTop: '6rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🤔</div>
          <h2 style={{ fontSize: '2rem' }}>{t('gameplay.hostWaitingNextQuestion')}</h2>
        </div>
      </div>
    );
  }

  // Question result screen
  if (questionResult) {
    return (
      <div style={{ minHeight: '100vh', width: '100%', background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)', display: 'flex', flexDirection: 'column' }}>
        <Header
          leftButton={onBackToGameSelection ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('gameplay.backToGameSelection') } : undefined}
          rightButton={{ icon: HeaderIcons.refresh, onClick: () => window.location.reload(), tooltip: t('common.tooltips.refreshPage') }}
          roomCode={roomState.roomName}
        />
        <WouldYouRatherResultScreen
          questionResult={questionResult}
          roomState={roomState}
          liveAnswers={sharedLiveAnswers}
          onBackToGameSelection={onBackToGameSelection}
          theme={THEME}
        />
      </div>
    );
  }

  // Question display
  const progress = currentQuestion ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);

  return (
    <div className="app">
      <Header
        leftButton={{ icon: HeaderIcons.back, onClick: onBackToGameSelection || (() => {}), tooltip: t('gameplay.backToGameSelection') }}
        rightButton={{ icon: HeaderIcons.refresh, onClick: handleRestartGame, tooltip: t('gameplay.restartGame') }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.gameLabel')}</span>
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#F8FAFC' }}>🤔 Would You Rather</span>
            </div>
            <div className="header-game-name" style={{ width: '1px', height: '24px', backgroundColor: 'rgba(255, 255, 255, 0.3)' }}></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div style={{ fontSize: '1.3rem', fontWeight: 'bold', letterSpacing: '0.15rem', color: THEME.primary, padding: '0.3rem 0.8rem', backgroundColor: THEME.primaryLight, borderRadius: '6px', border: `2px solid ${THEME.primaryBorder}` }}>
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />

      {/* Main game layout - desktop: left (question) | right (standings), mobile: stacked */}
      <div style={{
        minHeight: 'calc(100vh - 82px)',
        width: '100%',
        padding: '1rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
      }}>
        <DesktopLayout
          leftContent={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Main Challenge Display */}
        <div 
          className="container game-display-container" 
          style={{ 
            paddingTop: '1rem',
            paddingBottom: '1rem',
            marginTop: '1rem',
            marginBottom: 0,
          }}
        >
          <div className="timer-section" style={{ width: '100%', marginBottom: '1.2rem' }}>
            <TimerSection
              questionNumber={currentQuestion?.questionNumber}
              totalQuestions={currentQuestion?.totalQuestions}
              timeSeconds={secondsRemaining}
              progress={progress}
              primaryColor={THEME.primary}
            />
          </div>

          {/* Question text */}
          <h1 className="game-question-text" style={{
            color: THEME.primary,
            marginBottom: '1.5rem',
            letterSpacing: '0.03em',
            textShadow: `0 2px 8px ${THEME.primaryBorder}`,
          }}>
            {currentQuestion?.question}
          </h1>

          {/* Options – only when host is playing */}
          {hostIsPlaying && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
              marginTop: '1rem',
            }}>
              <button
                onClick={() => currentQuestion && handleVoteClick(currentQuestion.optionA)}
                disabled={voteSubmitted}
                style={{
                  padding: '1.5rem',
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  cursor: voteSubmitted ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                  border: selectedVote === currentQuestion?.optionA ? `3px solid ${THEME.optionA}` : '2px solid rgba(255,255,255,0.2)',
                  background: selectedVote === currentQuestion?.optionA ? THEME.optionALight : 'rgba(255,255,255,0.05)',
                  color: selectedVote === currentQuestion?.optionA ? THEME.optionA : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '2rem' }}>A</span>
                {currentQuestion?.optionA}
              </button>
              <button
                onClick={() => currentQuestion && handleVoteClick(currentQuestion.optionB)}
                disabled={voteSubmitted}
                style={{
                  padding: '1.5rem',
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  cursor: voteSubmitted ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                  border: selectedVote === currentQuestion?.optionB ? `3px solid ${THEME.optionB}` : '2px solid rgba(255,255,255,0.2)',
                  background: selectedVote === currentQuestion?.optionB ? THEME.optionBLight : 'rgba(255,255,255,0.05)',
                  color: selectedVote === currentQuestion?.optionB ? THEME.optionB : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '2rem' }}>B</span>
                {currentQuestion?.optionB}
              </button>
            </div>
          )}
        </div>
            </div>
          }
          rightContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              theme={THEME}
            />
          }
          mobileOnlyContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              isMobile={true}
              theme={THEME}
            />
          }
          rightWidth="320px"
        />
      </div>
    </div>
  );
};

export default WouldYouRatherHostDisplay;
